import { Navigate, useLocation } from 'react-router';
import type { ReactNode } from 'react';
import { useMe } from '../api/hooks.js';
import { AuthRequiredError } from '../api/client.js';
import { ImpersonationBanner } from './ImpersonationBanner.js';

// Wraps protected routes. Calls /api/auth/me via React Query; the
// query is cached so other components can read req.user without
// re-fetching. On 401 → redirect to /login (preserving the
// destination so a future "redirect after sign-in" iteration can
// land back there).

interface Props {
  children: ReactNode;
}

export function AuthGuard({ children }: Props) {
  const me = useMe();
  const location = useLocation();

  if (me.isLoading) {
    // Avoid layout flash — a small splash while /me resolves.
    return (
      <div className="login-shell" data-testid="auth-loading">
        <div className="login-card">
          <p style={{ margin: 0, color: 'var(--muted)' }}>Chargement…</p>
        </div>
      </div>
    );
  }

  if (me.error instanceof AuthRequiredError || !me.data) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (me.isError) {
    // A non-401 error from /me (network, 500). Surface it rather
    // than silently sending the user to /login.
    return (
      <div className="login-shell">
        <div className="login-card">
          <h1>Authentification indisponible</h1>
          <p>
            Impossible de joindre le serveur. Vérifiez votre connexion et
            rechargez la page.
          </p>
        </div>
      </div>
    );
  }

  // Le bandeau « Voir comme » vit ici : AuthGuard est le seul point
  // commun au shell staff et à l'Espace employé.
  return (
    <>
      <ImpersonationBanner />
      {children}
    </>
  );
}
